import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, RotateCcw, Copy, Eye, EyeOff, Check, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  sections: 'Bölmələr',
  labels: 'Hərflər',
  positions: 'Yerlər',
  sizes: 'Ölçülər',
  colors: 'Rənglər',
  shapes: 'Formalar',
  connections: 'Bağlantılar',
  table: 'Cədvəl',
  background: 'Arxa fon',
  grid: 'Hüceyrələr',
  gates: 'Qapılar',
  ai: 'AI şərh',
  public_toggle: 'Public/Private',
};
const PERMISSION_KEYS = Object.keys(PERMISSION_LABELS);
const PAGE_SIZE = 20;

const UsersEditor = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null); // 'new' | user._id
  const [form, setForm] = useState(null);
  const [flash, setFlash] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [analytics, setAnalytics] = useState({}); // { slug: {total, today, last_7_days, last_30_days, unique_ips_30d} }

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        ...auth,
        params: { skip, limit: PAGE_SIZE, q: q || undefined },
      });
      setUsers(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Yüklənə bilmədi' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, q, token]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Fetch per-user analytics once on mount and refresh whenever the list changes.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/visits/by-slug`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const row of res.data.items || []) map[row.slug] = row;
        setAnalytics(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, users.length]);

  const startCreate = () => {
    setEditingId('new');
    setForm({
      username: '',
      slug: '',
      password: '',
      is_active: true,
      is_public: false,
      permissions: Object.fromEntries(PERMISSION_KEYS.map((k) => [k, true])),
    });
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      username: u.username,
      slug: u.slug,
      is_active: u.is_active,
      is_public: u.is_public,
      permissions: { ...(u.permissions || {}) },
    });
  };

  const closeEditor = () => {
    setEditingId(null);
    setForm(null);
    setShowPwd(false);
  };

  const save = async () => {
    if (!form) return;
    try {
      if (editingId === 'new') {
        const res = await axios.post(`${API}/admin/users`, form, auth);
        const gen = res.data.generated_password;
        setFlash({
          kind: 'ok',
          msg: gen
            ? `Yeni istifadəçi yaradıldı. Parol: ${gen}  (kopyalayıb istifadəçiyə göndərin — bir daha göstərilməyəcək)`
            : 'Yeni istifadəçi yaradıldı',
        });
      } else {
        await axios.put(`${API}/admin/users/${editingId}`, form, auth);
        setFlash({ kind: 'ok', msg: 'Yeniləndi' });
      }
      closeEditor();
      reload();
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Saxlanılmadı' });
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Silinsin: ${u.username}? Bu userin config-i də silinəcək.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, auth);
      setFlash({ kind: 'ok', msg: 'Silindi' });
      reload();
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Silinmədi' });
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`${u.username} üçün parol sıfırlansın?`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${u.id}/reset-password`, {}, auth);
      const pw = res.data.password;
      setFlash({ kind: 'ok', msg: `Yeni parol: ${pw}  (kopyalayıb istifadəçiyə göndərin)` });
    } catch (e) {
      setFlash({ kind: 'err', msg: e.response?.data?.detail || 'Sıfırlana bilmədi' });
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlash({ kind: 'ok', msg: 'Kopyalandı' });
      setTimeout(() => setFlash(null), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">İstifadəçilər</h3>
      <p className="admin-section-desc">
        Hər user öz kabinetini idarə edir və <code>/u/&lt;slug&gt;</code> ünvanından ictimai matriks paylaşa bilər.
      </p>

      {flash && (
        <div
          data-testid="users-flash"
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            background: flash.kind === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(220,38,38,0.12)',
            border: `1px solid ${flash.kind === 'ok' ? 'rgba(34,197,94,0.4)' : 'rgba(220,38,38,0.4)'}`,
            color: flash.kind === 'ok' ? '#86efac' : '#fca5a5',
            borderRadius: 8,
            fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}
        >
          <span style={{ flex: 1 }}>{flash.msg}</span>
          {/parol/i.test(flash.msg) && (() => {
            const m = flash.msg.match(/parol:\s*(\S+)/i);
            return m ? (
              <button onClick={() => copyToClipboard(m[1])}
                style={{ padding: '4px 10px', border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.1)', color: '#fbbf24', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                <Copy size={12} style={{ display: 'inline', marginRight: 4 }} /> Kopyala
              </button>
            ) : null;
          })()}
          <button onClick={() => setFlash(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button onClick={startCreate} className="admin-btn-primary text-xs px-3 py-2 inline-flex items-center gap-1"
          data-testid="users-add">
          <Plus size={14} /> Yeni istifadəçi
        </button>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
          <input
            data-testid="users-search"
            className="karma-input text-left"
            style={{ paddingLeft: 32 }}
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            placeholder="İstifadəçi adı və ya slug…"
          />
        </div>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', alignSelf: 'center' }}>
          Cəmi: {total}
        </span>
      </div>

      {/* User list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {loading && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Yüklənir…</div>}
        {!loading && users.length === 0 && (
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>Heç bir istifadəçi yoxdur.</div>
        )}
        {users.map((u) => {
          const stats = analytics[u.slug];
          return (
          <div key={u.id} data-testid={`user-row-${u.slug}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, flexWrap: 'wrap',
            }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 14, color: 'white', fontWeight: 600 }}>
                {u.username}
                {!u.is_active && (
                  <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 6px', background: 'rgba(220,38,38,0.15)', color: '#fca5a5', borderRadius: 4 }}>deaktiv</span>
                )}
                {u.is_public && (
                  <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 6px', background: 'rgba(34,197,94,0.15)', color: '#86efac', borderRadius: 4 }}>public</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                /u/{u.slug} • /dashboard
              </div>
              {stats && (
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: 'rgba(147,197,253,0.85)' }}>
                  <span title="Ümumi ziyarətlər">👁 {stats.total}</span>
                  <span title="Bu gün">🕐 {stats.today}</span>
                  <span title="Son 7 gün">📅 {stats.last_7_days}</span>
                  <span title="Son 30 gün">📊 {stats.last_30_days}</span>
                  <span title="Unikal IP (son 30 gün)">🌐 {stats.unique_ips_30d}</span>
                </div>
              )}
            </div>
            <button onClick={() => startEdit(u)} className="text-xs px-3 py-1.5 rounded border border-white/20 hover:bg-white/5 text-white/80" data-testid={`user-edit-${u.slug}`}>
              Redaktə
            </button>
            <button onClick={() => resetPassword(u)} className="text-xs px-3 py-1.5 rounded border border-yellow-500/40 hover:bg-yellow-500/10 text-yellow-300" data-testid={`user-reset-${u.slug}`}>
              <RotateCcw size={12} style={{ display: 'inline', marginRight: 4 }} /> Parol sıfırla
            </button>
            <button onClick={() => remove(u)} className="text-xs px-3 py-1.5 rounded border border-red-500/40 hover:bg-red-500/10 text-red-400" data-testid={`user-delete-${u.slug}`}>
              <Trash2 size={12} />
            </button>
          </div>
          );
        })}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, justifyContent: 'center' }}>
          <button
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            className="text-xs px-3 py-1.5 rounded border border-white/20 disabled:opacity-40"
          >
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
            {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} / {total}
          </span>
          <button
            disabled={skip + PAGE_SIZE >= total}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            className="text-xs px-3 py-1.5 rounded border border-white/20 disabled:opacity-40"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Edit / Create modal */}
      {editingId && form && (
        <div
          onClick={closeEditor}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="users-editor-modal"
            style={{
              width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto',
              background: 'linear-gradient(135deg, rgba(20,10,40,0.98), rgba(10,10,25,0.98))',
              border: '1px solid rgba(251,191,36,0.3)', borderRadius: 12, padding: 24, color: 'white',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontFamily: "'Cinzel', serif", fontSize: 20, color: '#fbbf24' }}>
                {editingId === 'new' ? 'Yeni istifadəçi' : 'Redaktə'}
              </h3>
              <button onClick={closeEditor} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>İstifadəçi adı *</span>
                <input
                  data-testid="user-form-username"
                  className="karma-input text-left"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
              </label>
              {editingId === 'new' && (
                <>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                      Slug (boş buraxılsa <code>{form.username || 'istifadəçi'}</code>-dan yaradılır)
                    </span>
                    <input
                      className="karma-input text-left font-mono"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      placeholder="ali-suleymanli"
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                      Parol (boş buraxılsa avtomatik yaradılır)
                    </span>
                    <div style={{ position: 'relative' }}>
                      <input
                        data-testid="user-form-password"
                        className="karma-input text-left"
                        type={showPwd ? 'text' : 'password'}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                      <button type="button" onClick={() => setShowPwd(!showPwd)}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                        {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </label>
                </>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <span style={{ fontSize: 13 }}>Aktiv</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
                <span style={{ fontSize: 13 }}>Public matriks (parolsuz görünsün)</span>
              </label>
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'rgba(255,255,255,0.8)' }}>İcazələr</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 6 }}>
                {PERMISSION_KEYS.map((k) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.03)' }}>
                    <input type="checkbox"
                      checked={form.permissions?.[k] !== false}
                      onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [k]: e.target.checked } })}
                    />
                    <span style={{ fontSize: 12 }}>{PERMISSION_LABELS[k]}</span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button onClick={() => setForm({ ...form, permissions: Object.fromEntries(PERMISSION_KEYS.map(k => [k, true])) })}
                  className="text-xs px-2 py-1 rounded border border-white/15 text-white/70">Hamısı</button>
                <button onClick={() => setForm({ ...form, permissions: Object.fromEntries(PERMISSION_KEYS.map(k => [k, false])) })}
                  className="text-xs px-2 py-1 rounded border border-white/15 text-white/70">Heç biri</button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <button onClick={closeEditor} className="text-sm px-4 py-2 rounded border border-white/20 text-white/80">Ləğv et</button>
              <button data-testid="user-form-save" onClick={save} className="admin-btn-primary text-sm px-4 py-2 inline-flex items-center gap-1">
                <Check size={14} /> Saxla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersEditor;
